import React, {
  useEffect,
  useState,
  useRef,
  useMemo,
  useCallback,
} from "react";
import { ButtonBase } from "@reusejs/react";
import { useRouter } from "next/router";
// import { useBottomScrollListener } from "react-bottom-scroll-listener";
import { useInView } from "react-intersection-observer";
import {
  getSearchPayloadForEvents,
  getSearchResults,
  getAuctionEventsWithPagination,
} from "services";
import AuctionEventCard from "./EventCard";
import { useSelector } from "react-redux";
import Pusher from "pusher-js";
import { ReuseInputGroup } from "@locoworks/reusejs-react-input-group";
import { BetaForm as useBetaForm } from "@reusejs/react";
import Loader from "../../../layouts/Loader";
import CalendarInput from "../../Inputs/CalendarInput";
import { PickerSelectSimple } from "@reusejs/react";
import useDebounce from "../../utils/useDebounce";

interface Event {
  status: string;
  property_dump: any;
  property: any;
}

export default function ViewEvents() {
  const router = useRouter();
  const [events, setEvents] = useState<Event[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [activeTabIndex, setActiveTabIndex] = useState(0);
  const [isFetchingMore, setIsFetchingMore] = useState(false);
  const [activeStatus, setActiveStatus] = useState<string | null>(null);
  const { ref: scrollRef, inView } = useInView({
    threshold: 0.5,
  });

  const currentBank = useSelector((state: any) => state.bankerAuth.bank);
  const currentBranch = useSelector(
    (state: any) => state.bankerAuth.currentBranch
  );

  const searchForm = useBetaForm({
    searchQuery: "",
    startDate: "",
    endDate: "",
    status: "",
  });

  const valueRef = useRef<string>("");

  const handleGetAuctionEvents = useCallback(
    async (shouldShowNextPage: boolean, status: string | null = null) => {
      if (isFetchingMore || (shouldShowNextPage && page >= totalPages)) return;

      setIsFetchingMore(true);
      const currentPage = shouldShowNextPage ? page + 1 : 1;

      const preparedPayload: any = {
        searchQuery: valueRef.current,
        startDate: searchForm.value.startDate
          ? `${searchForm.value.startDate}T00:00:00.000Z`
          : "",
        endDate: searchForm.value.endDate
          ? `${searchForm.value.endDate}T23:59:00.000Z`
          : "",
        status: status ? status : "", // || searchForm.value.status,
        page: currentPage,
      };

      try {
        if (!shouldShowNextPage) setIsLoading(true);
        const res: any = await getAuctionEventsWithPagination(preparedPayload);

        setTotalPages(res.meta.total_page);
        setPage(currentPage);

        setEvents((prev) =>
          shouldShowNextPage ? [...prev, ...res.data] : res.data
        );
      } catch (error) {
        console.error("Error fetching auction events:", error);
      } finally {
        setIsLoading(false);
        setIsFetchingMore(false);
      }
    },
    [
      page,
      totalPages,
      currentBank.uuid,
      currentBranch.value,
      searchForm.value,
      isFetchingMore,
    ]
  );

  useEffect(() => {
    if (inView && !isLoading && !isFetchingMore) {
      handleGetAuctionEvents(true, activeStatus);
    }
  }, [inView, isLoading, isFetchingMore, handleGetAuctionEvents, activeStatus]);

  useEffect(() => {
    handleGetAuctionEvents(false, activeStatus);
  }, [
    activeStatus,
    currentBranch,
    searchForm.value.startDate,
    searchForm.value.endDate,
    searchForm.value.status,
  ]);

  const clearFilter = () => {
    searchForm.setField("startDate", "");
    searchForm.setField("endDate", "");
    searchForm.setField("status", "");
    searchForm.setField("searchQuery", "");
    valueRef.current = "";
  };

  const handleTabChange = (index: number) => {
    if (index !== activeTabIndex) {
      clearFilter();
      setActiveTabIndex(index);
      let status: string | null = null;
      switch (index) {
        case 0: // All Events
          status = null;
          break;
        case 1: // Live Events
          status = "live";
          break;
      }

      setActiveStatus(status);
      setPage(1);
      setEvents([]);
    }
  };

  const handleSearchQuery = useDebounce(
    () => handleGetAuctionEvents(false, activeStatus),
    200
  );

  useEffect(() => {
    handleSearchQuery();
  }, [valueRef.current]);

  useEffect(() => {
    Pusher.logToConsole = false;
    const pusher = new Pusher(`${process.env.NEXT_PUBLIC_PUSHER_KEY}`, {
      cluster: process.env.NEXT_PUBLIC_PUSHER_CLUSTER,
    });

    const channelId = `update-dashboard`;
    const channel = pusher.subscribe(channelId);

    channel.bind("auction_event_has_started", (data: any) => {
      if (data) {
        handleGetAuctionEvents;
      }
    });

    channel.bind("auction_event_has_ended", (data: any) => {
      if (data) {
        handleGetAuctionEvents;
      }
    });

    pusher.connection.bind("error", function (err: any) {
      if (err?.error?.data?.code === 4004) {
        console.log(">>> detected limit error");
      } else if (err?.data?.code === 1006) {
        // Websocket: CLOSED ABNORMALLY
      }
    });

    pusher.connection.bind("disconnected", function (err: any) {
      console.log(">>> disconnected");
    });

    pusher.connection.bind("connected", function (err: any) {
      console.log("connected", pusher.sessionID);
    });

    return function cleanup() {
      pusher.disconnect();
    };
  }, [currentBranch]);

  const generateDropdownValues = () => [
    { value: "cancelled", label: "Cancelled" },
    { value: "end", label: "Ended" },
    { value: "pending", label: "Pending" },
    { value: "live", label: "Live" },
    { value: "rejected", label: "Rejected" },
    { value: "accepted", label: "Accepted" },
  ];

  const filteredEvents = useMemo(() => {
    const filterByStatus = (status: string) =>
      events.filter((item) => item.status === status);
    return {
      live: filterByStatus("live"),
      upcoming: filterByStatus("accepted"),
      past: filterByStatus("end"),
      cancelled: filterByStatus("cancelled"),
    };
  }, [events]);

  const renderEventList = (eventList: Event[]) => (
    <div className="w-full p-8 mt-4 space-y-6">
      {eventList.length > 0 &&
        eventList.map((item, index) => (
          <AuctionEventCard
            key={index}
            property={item.property_dump || item.property}
            event={item}
            callback={() => handleGetAuctionEvents(false)}
          />
        ))}
      {eventList.length > 0 && (
        <div ref={scrollRef} className="h-10" /> // Intersection observer target
      )}
      {isFetchingMore && (
        <div className="w-full flex justify-center">
          <Loader height="w-8 h-8" width="w-8" color="text-blue-600" />
        </div>
      )}
    </div>
  );

  return (
    <div className="w-full p-6 flex flex-col justify-center">
      {/* Header */}
      <div className="min-w-full flex justify-between mb-6">
        <div className="w-1/2 text-4xl text-blue-600 font-extrabold dark:text-red-600">
          Auction Events
        </div>
        <div className="w-1/2 flex justify-end">
          <ButtonBase
            variant="primary"
            type="button"
            label="Create Event"
            onClick={() =>
              router.push("/dashboard/events/create", undefined, {
                shallow: true,
              })
            }
            buttonBaseClasses={{
              font: "text-sm font-medium",
              backgroundColor: "bg-blue-600 hover:bg-blue-700",
              textColor: "text-white",
              border: "any",
              padding: "px-4 py-3",
            }}
          />
        </div>
      </div>

      {/* Search and Filters */}
      <div className="max-w-[900px] flex flex-col justify-between mb-6">
        <ReuseInputGroup
          wrapperClasses="w-full mb-3"
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
            searchForm.setField("searchQuery", e.target.value);
            valueRef.current = e.target.value;
          }}
          value={searchForm.value.searchQuery}
          className="pl-4 pr-14 border border-gray-400"
          headerText="Search"
          helperStyles="mt-2"
          placeholder="Search by Event ID, Borrower Name.."
        />
        <div className="flex gap-2 flex-1 w-full">
          <CalendarInput
            wrapperClassName="w-full"
            className="w-full"
            searchForm={searchForm}
            dateName="startDate"
            headerText="Start Date"
          />
          <CalendarInput
            wrapperClassName="w-full"
            className="w-full"
            searchForm={searchForm}
            dateName="endDate"
            headerText="End Date"
          />
          <div className="w-full">
            <label className="font-bold text-lg">Status</label>
            <PickerSelectSimple
              dataSource={async () => generateDropdownValues()}
              enableToggle
              defaultString="Select Status"
              defaultSelected={
                searchForm.value.status ? [searchForm.value.status] : []
              }
              pickerSelectBaseClasses={{
                wrapper:
                  "border-2 border-red-200 bg-red-200 px-[200px] max-w-[30em]",
              }}
              pickerSelectSimpleClasses={{
                selectedDataClasses: {
                  placeholderClasses:
                    "font-normal text-md text-black-400 pickerasdfd",
                  spanBaseClasses: "text-md font-normal leading-2",
                },
                optionClasses: {
                  label: "text-md bg-blue-200",
                },
              }}
              onChange={(value: any) => {
                searchForm.setField("status", value);
                setActiveStatus(value.value);
              }}
              valueKey="value"
              multiple={false}
              enableClear={false}
              enableClose={false}
              enableSearch={false}
              disabled={activeTabIndex === 1 ? true : false}
            />
          </div>
          <div className="w-full flex items-end justify-end">
            <ButtonBase
              variant="primary"
              type="button"
              label="Clear Filter"
              onClick={() => {
                clearFilter();
                setActiveStatus(null);
                setActiveTabIndex(0);
              }}
              buttonBaseClasses={{
                font: "text-sm font-medium block",
                backgroundColor: "bg-blue-600 hover:bg-blue-700",
                textColor: "text-white",
                border: "any",
                padding: "px-4 py-3",
              }}
            />
          </div>
        </div>
      </div>

      {/* Event Tabs */}
      {isLoading ? (
        <div className="h-[9000px] flex items-start pt-[300px] justify-center">
          <Loader
            height="w-10 md:h-16"
            width="w-10 md:w-16"
            color="text-blue-600"
          />
        </div>
      ) : (
        <TabsBase
          elements={[
            {
              title: <div className="flex items-center px-2">All Events</div>,
              content: (
                <div className="flex w-full justify-center border-2 border-t-0 rounded-b-lg bg-gray-50 border-gray-200">
                  {renderEventList(events)}
                </div>
              ),
            },
            {
              title: <div className="flex items-center px-2">Live Events</div>,
              content: (
                <div className="flex w-full justify-center border-2 border-t-0 rounded-b-lg bg-gray-50 border-gray-200">
                  {renderEventList(filteredEvents.live)}
                </div>
              ),
            },
          ]}
          activeTabIndex={activeTabIndex}
          setActiveTabIndex={handleTabChange}
        />
      )}
    </div>
  );
}

export interface TabsBaseProps {
  elements: {
    title: React.ReactNode;
    content: React.ReactNode;
    icon?: React.ReactNode;
  }[];
  activeTabIndex: number;
  setActiveTabIndex: (index: number) => void;
}

const TabsBase = (props: TabsBaseProps) => {
  return (
    <div className="flex flex-col">
      <div
        className={
          "flex h-full w-full items-end overflow-scroll scroll-smooth scrollbar-hide pt-16"
        }
      >
        {props.elements.map((element, index) => {
          return (
            <div
              className={
                "flex items-center justify-center h-full w-full min-w-fit bg-white px-2 cursor-pointer rounded-t-lg border-2 border-gray-200" +
                (index === props.activeTabIndex
                  ? " sticky left-0 right-0 bg-gray-50 border-b-0 scale-y-125 origin-bottom font-bold"
                  : " border-gray-200")
              }
              onClick={() => {
                props.setActiveTabIndex(index);
              }}
              key={"Element" + index}
            >
              {element.title}
              {element.icon && element.icon}
            </div>
          );
        })}
      </div>
      {props.elements[props.activeTabIndex].content}
    </div>
  );
};
